import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useMemo, useState} from 'react';
import type {KnowledgeArticleResponse} from 'src/api/ProactApiClient';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {SidebarLinkKnowledgeBase} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {useAppSelector} from 'src/redux/hooks';
import {
  useCreateKnowledgeArticleMutation,
  useGetKnowledgeArticleQuery,
  useUpdateKnowledgeArticleMutation,
} from 'src/redux/queries/knowledgeBase';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {BackBar} from 'src/app/layout/BackBar';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {
  KnowledgeArticleFormMeta,
  KnowledgeArticleToFormData,
  KnowledgeBaseDocumentTitle,
  MapKnowledgeArticleResponseToRequest,
} from './knowledgeArticleHelpers';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface KnowledgeArticleFormProps {
  id?: number;
  mode: 'create' | 'edit';
}

export const KnowledgeArticleForm: React.FC<KnowledgeArticleFormProps> = ({
  id,
  mode,
}) => {
  const {setAlert} = useAlert();
  const navigate = useAppNavigate();
  const [isSavePending, setIsSavePending] = useState(false);
  const isSuperAdmin = useAppSelector(state => state.session.isSuperAdmin);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const {data: fetchedItem, isLoading: isItemLoading} =
    useGetKnowledgeArticleQuery(id!, {
      skip: !isEditMode || id === undefined,
    });

  const [createArticle] = useCreateKnowledgeArticleMutation();
  const [updateArticle] = useUpdateKnowledgeArticleMutation();

  const item: KnowledgeArticleResponse | undefined = fetchedItem;
  const isLoading = isEditMode && isItemLoading;

  const formMeta = useMemo(
    () => KnowledgeArticleFormMeta(isSuperAdmin),
    [isSuperAdmin]
  );

  const form = useEntitySyncedForm({
    formMeta,
    entity: fetchedItem,
    toFormData: KnowledgeArticleToFormData,
  });

  const handleSave = async () => {
    setIsSavePending(true);
    try {
      const model = MapKnowledgeArticleResponseToRequest(
        item,
        form.getFieldValues()
      );

      const response = isCreateMode
        ? await createArticle(model).unwrap()
        : await updateArticle(model).unwrap();

      setAlert(
        `${response.title} was successfully ${
          isCreateMode ? 'created' : 'updated'
        }`,
        'success'
      );
      navigate(PathUtility.KnowledgeBaseView(response.id), {replace: true});
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    if (await form.validateForm()) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.KnowledgeBaseView(id)
      : PathUtility.KnowledgeBase()
  );

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const pageTitle = isCreateMode
    ? 'New Article'
    : item?.title ?? 'Edit Article';

  return (
    <Stack>
      <BackBar
        fallbackTo={
          isEditMode && id
            ? PathUtility.KnowledgeBaseView(id)
            : PathUtility.KnowledgeBase()
        }
        fallbackLabel={
          isEditMode ? 'Back to Article' : 'Back to Knowledge Base'
        }
        breadcrumbs={[
          {
            label: KnowledgeBaseDocumentTitle,
            to: PathUtility.KnowledgeBase(),
          },
          ...(isEditMode && item
            ? [
                {
                  label: item.title,
                  to: PathUtility.KnowledgeBaseView(id!),
                },
              ]
            : []),
          {label: isCreateMode ? 'New Article' : 'Edit'},
        ]}
      />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkKnowledgeBase.icon}>
          {pageTitle}
        </PageHeader.Title>

        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}
      </PageHeader>

      <SectionCard title="Article">
        <Box p="md">
          <ProactForm form={form}>
            <ProactFormGrid />
          </ProactForm>
        </Box>
      </SectionCard>

      <Group>
        <PageHeader.Action variant="cancel" onClick={handleCancel}>
          Cancel
        </PageHeader.Action>
        <PageHeader.Action
          variant={isCreateMode ? 'create' : 'save'}
          onClick={handleValidateAndSave}
          loading={isSavePending}>
          {isCreateMode ? 'Create Article' : 'Save Changes'}
        </PageHeader.Action>
      </Group>
    </Stack>
  );
};
